import React, { useState, useRef } from 'react';
import { storeGetOrDefault, storeSet, DEF_HERO } from '../../store';
import '../admin.css';

const empty = () => ({ id: Date.now(), img: '', tag: '', title: '', caption: '', btn1Text: 'Explore Products', btn1Link: '/products', btn2Text: 'Get a Quote', btn2Link: '/contact' });

const Toast = ({ msg, type }) => msg ? <div className={`adm-toast adm-toast-${type}`}>{msg}</div> : null;

const HeroAdmin = () => {
  const [slides, setSlides] = useState(() => storeGetOrDefault('hero', DEF_HERO));
  const [mode, setMode]     = useState(null);
  const [form, setForm]     = useState(empty());
  const [toast, setToast]   = useState({ msg: '', type: 'success' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  const save = (list) => { setSlides(list); storeSet('hero', list); };

  const openAdd  = () => { setForm(empty()); setMode('add'); };
  const openEdit = (s) => { setForm({ ...s }); setMode('edit'); };
  const close    = () => { setMode(null); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return showToast('Title is required.', 'error');
    if (mode === 'add') {
      save([...slides, { ...form, id: Date.now() }]);
      showToast('Slide added!');
    } else {
      save(slides.map(s => s.id === form.id ? { ...form } : s));
      showToast('Slide updated!');
    }
    close();
  };

  const del  = (id) => { if (window.confirm('Delete this slide?')) { save(slides.filter(s => s.id !== id)); showToast('Slide deleted.', 'info'); } };

  const move = (idx, dir) => {
    const list = [...slides]; const swap = idx + dir;
    if (swap < 0 || swap >= list.length) return;
    [list[idx], list[swap]] = [list[swap], list[idx]];
    save(list);
  };

  const field = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const imgRef = useRef(null);
  const handleImgUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return showToast('Please select an image file.', 'error');
    if (file.size > 5 * 1024 * 1024) return showToast('Image must be under 5 MB.', 'error');
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, img: ev.target.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className='adm-page'>
      <Toast {...toast} />
      <div className='adm-header'>
        <div>
          <h1 className='adm-title'>Hero Slider</h1>
          <p className='adm-subtitle'>{slides.length} slides — displayed in carousel order</p>
        </div>
        <button className='adm-btn adm-btn-primary' onClick={openAdd}>+ Add Slide</button>
      </div>

      <div className='adm-card-grid'>
        {slides.map((s, idx) => (
          <div className='adm-slide-card' key={s.id}>
            {s.img
              ? <img src={s.img} alt={s.title} className='adm-slide-card-img' />
              : <div style={{ height: 140, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8', fontSize: '1.5rem' }}>🖼</div>
            }
            <div className='adm-slide-card-body'>
              <div className='adm-slide-card-tag'>{s.tag || '—'}</div>
              <h3 className='adm-slide-card-title'>{s.title}</h3>
              <p className='adm-slide-card-caption'>{s.caption}</p>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <button className='adm-order-btn' style={{ padding: '4px 8px' }} onClick={() => move(idx, -1)} disabled={idx === 0}>▲ Up</button>
                <button className='adm-order-btn' style={{ padding: '4px 8px' }} onClick={() => move(idx, 1)}  disabled={idx === slides.length - 1}>▼ Down</button>
              </div>
              <div className='adm-slide-card-footer'>
                <button className='adm-btn adm-btn-outline adm-btn-sm' onClick={() => openEdit(s)}>Edit</button>
                <button className='adm-btn adm-btn-danger  adm-btn-sm' onClick={() => del(s.id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
        {slides.length === 0 && (
          <div className='adm-empty'><div className='adm-empty-icon'>🖼</div><p>No slides added yet.</p></div>
        )}
      </div>

      {mode && (
        <div className='adm-modal-overlay' onClick={e => e.target === e.currentTarget && close()}>
          <div className='adm-modal adm-modal-lg'>
            <div className='adm-modal-header'>
              <h2 className='adm-modal-title'>{mode === 'add' ? 'Add Slide' : 'Edit Slide'}</h2>
              <button className='adm-close-btn' onClick={close}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className='adm-modal-body'>
                <div className='adm-form'>
                  <div className='adm-form-group'>
                    <label className='adm-label'>Slide Background Image</label>
                    <div className='adm-img-upload-zone' onClick={() => imgRef.current?.click()}>
                      {form.img
                        ? <img src={form.img} alt='preview' className='adm-upload-preview' onError={e => { e.target.style.display='none'; }} />
                        : <div className='adm-upload-placeholder'>
                            <div className='icon'>🖼️</div>
                            <span>Click to Upload Image</span>
                            <p>PNG, JPG, WEBP — max 5 MB</p>
                          </div>
                      }
                    </div>
                    <input ref={imgRef} type='file' accept='image/*' style={{ display: 'none' }} onChange={handleImgUpload} />
                    <div className='adm-img-or'>or paste image URL below</div>
                    <input className='adm-input' value={form.img} onChange={field('img')} placeholder='https://images.unsplash.com/...' />
                  </div>
                  <div className='adm-form-row'>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Tag / Eyebrow</label>
                      <input className='adm-input' value={form.tag} onChange={field('tag')} placeholder='Proven Protection' />
                    </div>
                    <div className='adm-form-group'>
                      <label className='adm-label adm-label-req'>Title</label>
                      <input className='adm-input' value={form.title} onChange={field('title')} placeholder='ZINGA® Cold Galvanising' required />
                    </div>
                  </div>
                  <div className='adm-form-group'>
                    <label className='adm-label'>Caption</label>
                    <textarea className='adm-input adm-textarea' value={form.caption} onChange={field('caption')} placeholder='Short description shown on slide' />
                  </div>
                  <div className='adm-form-row'>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Button 1 Text</label>
                      <input className='adm-input' value={form.btn1Text} onChange={field('btn1Text')} />
                    </div>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Button 1 Link</label>
                      <input className='adm-input' value={form.btn1Link} onChange={field('btn1Link')} placeholder='/products' />
                    </div>
                  </div>
                  <div className='adm-form-row'>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Button 2 Text</label>
                      <input className='adm-input' value={form.btn2Text} onChange={field('btn2Text')} />
                    </div>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Button 2 Link</label>
                      <input className='adm-input' value={form.btn2Link} onChange={field('btn2Link')} placeholder='/contact' />
                    </div>
                  </div>
                </div>
              </div>
              <div className='adm-modal-footer'>
                <button type='button' className='adm-btn adm-btn-ghost' onClick={close}>Cancel</button>
                <button type='submit' className='adm-btn adm-btn-primary'>{mode === 'add' ? 'Add Slide' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroAdmin;
